import { useCallback, useEffect, useRef, useState } from "react";

import { useCaptcha } from "@smart/bridge-captcha-dom";
import { useIdentify } from "@smart/bridge-feature-flags-dom";
import {
  AutoFillStatus,
  FieldItem,
  useToast,
} from "@smart/bridge-intake-components-dom";
import { identifyTrackingUser, trackChanged } from "@smart/bridge-metrics-dom";
import { Loading } from "@smart/itops-sb-design-system-dom";
import { DIGITAL_SIGNATURE_RETURN_TOKEN_PARAM } from "@smart/itops-utils-basic";

import { SubmissionForm } from "./form";
import { intakeApi, IntakeApiForm, useSubmissionAnalytics } from "../../hooks";
import { NotFound } from "../not-found";

export type SubmissionLoadViewProps = {
  id: string;
  hideTeam?: boolean;
};

export const SubmissionLoadView = ({
  id,
  hideTeam,
}: SubmissionLoadViewProps) => {
  const returnToken = new URLSearchParams(window.location.search).get(
    DIGITAL_SIGNATURE_RETURN_TOKEN_PARAM,
  );

  const {
    result,
    status,
    fetch: loadSubmission,
    refetch: reloadSubmission,
  } = intakeApi.load({
    body: returnToken ? { id, returnToken } : { id },
    retry: 10,
    checkShouldRetry: async (res) => {
      try {
        const { submission } = await res.json();
        return !submission;
      } catch {
        return true;
      }
    },
  });
  const { identifyFeatureFlagUser, identified: featureFlagUserIdentified } =
    useIdentify();
  const { setToasts } = useToast();
  const captchaContext = useCaptcha();
  const [autoFillStatus, setAutoFillStatus] = useState<AutoFillStatus>();
  // We need to store the fields so they can be updated when the dynamic options are loaded
  const [fields, setFields] = useState<IntakeApiForm["fields"]>([]);
  const [selectedLanguage, setSelectedLanguage] = useState<string>("en");
  const previousLanguageRef = useRef("en");
  const { postTrackEvent, postIdentifyEvent } = useSubmissionAnalytics(id);

  const setSelectedLanguageWithAnalytics = useCallback(
    (next: string) => {
      const languageLabel = result?.form?.supportedLanguages?.find(
        (l) => l.key === next,
      )?.label;
      if (
        result?.form &&
        languageLabel &&
        next !== previousLanguageRef.current
      ) {
        postTrackEvent({
          category: "track",
          name: "IntakeLanguagesEndClientSelected",
          screen: "Submission",
          entryPoint: "IntakeSubmission",
          component: "Languages",
          jiraKey: "LINK-3383",
          interactionType: "Active",
          properties: {
            languageSelected: languageLabel,
            formName: result.form.title ?? "",
          },
        });
      }
      previousLanguageRef.current = next;
      setSelectedLanguage(next);
    },
    [postTrackEvent, result?.form],
  );

  trackChanged(result, "Submission Loaded", (r) => {
    if (!r || !r.form || !r.submission) return undefined;

    if (r.team) {
      identifyTrackingUser({
        source: r.team.source,
        team: { uri: r.team.uri, name: r.team.name },
      });
      identifyFeatureFlagUser({
        team: {
          uri: r.team.uri,
          name: r.team.name,
          provider: r.team.source,
        },
      });
      postIdentifyEvent();
    }

    return {
      formUri: r.form.uri,
      submissionUri: r.submission.uri,
    };
  });

  useEffect(() => {
    if (result?.form?.fields) {
      setFields(result.form.fields);
    }
  }, [!!result?.form?.fields]);

  const reloadForSigningUrl = useCallback(async () => {
    const loaded = await loadSubmission();
    return loaded?.signingUrl;
  }, [loadSubmission]);

  if (
    autoFillStatus !== "filling" &&
    (status === "initial" || status === "loading")
  )
    return <Loading />;

  if (result) {
    const {
      matter,
      team,
      form,
      submission,
      staffDetails,
      aiUserFeedback,
      branding,
      voiceGuide,
      hasPendingDigitalSignature,
      signingUrl,
    } = result;
    if (team && form && submission)
      return (
        <SubmissionForm
          matter={matter}
          team={team}
          form={{ ...form, fields }}
          submission={submission}
          aiUserFeedback={aiUserFeedback}
          autoFillStatus={autoFillStatus}
          setAutoFillStatus={setAutoFillStatus}
          hideTeam={hideTeam}
          staffDetails={staffDetails || []}
          loadResponses={async () => {
            const loaded = await loadSubmission();
            return loaded?.submission?.responses;
          }}
          reloadSubmission={reloadSubmission}
          reloadForSigningUrl={reloadForSigningUrl}
          featureFlagUserIdentified={featureFlagUserIdentified}
          captchaToken={captchaContext?.token}
          setToasts={setToasts}
          setFields={
            setFields as (setFn: (fields: FieldItem[]) => FieldItem[]) => void
          }
          branding={branding}
          voiceGuide={voiceGuide}
          selectedLanguage={selectedLanguage}
          setSelectedLanguage={setSelectedLanguageWithAnalytics}
          hasPendingDigitalSignature={!!hasPendingDigitalSignature}
          signingUrl={signingUrl}
        />
      );
  }

  return <NotFound />;
};
